import { type Locale, LOCALE_PREFIX } from './index'

/** Pages statiques (un composant fixe par page). */
export type StaticPageKey =
  | 'home'
  | 'about'
  | 'contact'
  | 'blog'
  | 'sectors'
  | 'service.storage'
  | 'service.pickpack'
  | 'service.shipping'
  | 'service.returns'
  | 'fulfillment'
  | 'pricing'
  | 'expertise.adr'
  | 'expertise.afsca'
  | 'privacy'
  | 'terms'
  | 'legal'
  | 'cookies'

/** Pages dynamiques (slug variable : article de blog, détail secteur). */
export type DynamicPageKey = 'blog.article' | 'sectors.detail'

export type PageKey = StaticPageKey | DynamicPageKey

/** Slug par langue pour chaque page statique. 'home' = chaîne vide (racine). */
export const ROUTES: Record<StaticPageKey, Record<Locale, string>> = {
  home: { fr: '', en: '', nl: '' },
  about: { fr: 'about', en: 'about', nl: 'over-ons' },
  contact: { fr: 'contact', en: 'contact', nl: 'contact' },
  blog: { fr: 'blog', en: 'blog', nl: 'blog' },
  sectors: { fr: 'secteurs', en: 'sectors', nl: 'sectoren' },
  'service.storage': { fr: 'stockage', en: 'storage', nl: 'opslag' },
  'service.pickpack': { fr: 'pick-pack', en: 'pick-pack', nl: 'pick-pack' },
  'service.shipping': {
    fr: 'expedition-24h', en: 'next-day-delivery', nl: 'levering-volgende-dag',
  },
  'service.returns': { fr: 'retours', en: 'returns', nl: 'retourzending' },
  fulfillment: {
    fr: 'fulfillment-dnvb-d2c', en: 'dnvb-d2c-fulfillment', nl: 'dnvb-d2c-fulfilment',
  },
  pricing: { fr: 'tarifs', en: 'pricing', nl: 'tarieven' },
  'expertise.adr': { fr: 'expertise/adr', en: 'expertise/adr', nl: 'expertise/adr' },
  'expertise.afsca': { fr: 'expertise/afsca', en: 'expertise/afsca', nl: 'expertise/afsca' },
  privacy: { fr: 'privacy', en: 'privacy', nl: 'privacy' },
  terms: { fr: 'cgv', en: 'terms', nl: 'algemene-voorwaarden' },
  legal: {
    fr: 'mentions-legales', en: 'legal-notice', nl: 'juridische-kennisgeving',
  },
  cookies: { fr: 'cookies', en: 'cookies', nl: 'cookies' },
}

/**
 * Slug par langue pour les pages dynamiques : segment localisé + ':slug'.
 * Le segment reflète le même slug que la page liste (`blog`, `secteurs`…).
 */
export const DYNAMIC_ROUTES: Record<DynamicPageKey, Record<Locale, string>> = {
  'blog.article': { fr: 'blog/:slug', en: 'blog/:slug', nl: 'blog/:slug' },
  'sectors.detail': { fr: 'secteurs/:slug', en: 'sectors/:slug', nl: 'sectoren/:slug' },
}

/** Table fusionnée statique + dynamique (lookup interne par localizedPath). */
const ALL_ROUTES: Record<PageKey, Record<Locale, string>> = {
  ...ROUTES,
  ...DYNAMIC_ROUTES,
}

/** Construit le chemin localisé d'une page. Remplace `:param` via `params`. */
export function localizedPath(
  key: PageKey,
  locale: Locale,
  params?: Record<string, string>,
): string {
  const slug = ALL_ROUTES[key][locale]
  const prefix = LOCALE_PREFIX[locale]
  // Page d'accueil : juste le préfixe (ou '/').
  if (slug === '') return prefix === '' ? '/' : prefix
  let path = `${prefix}/${slug}`
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      path = path.replace(`:${k}`, v)
    }
  }
  if (import.meta.env?.DEV && path.includes('/:')) {
    console.warn(`[i18n] localizedPath: paramètre manquant pour la route '${key}' → ${path}`)
  }
  return path
}

/** Toutes les URLs alternatives d'une page (pour hreflang). */
export function alternatesFor(
  key: PageKey,
  params?: Record<string, string>,
): Record<Locale, string> {
  return {
    fr: localizedPath(key, 'fr', params),
    en: localizedPath(key, 'en', params),
    nl: localizedPath(key, 'nl', params),
  }
}

/** Alternates hreflang pour une page dynamique dont le slug diffère par langue. */
export function dynamicAlternates(
  key: DynamicPageKey,
  slugByLocale: Record<Locale, string>,
): Record<Locale, string> {
  return {
    fr: localizedPath(key, 'fr', { slug: slugByLocale.fr }),
    en: localizedPath(key, 'en', { slug: slugByLocale.en }),
    nl: localizedPath(key, 'nl', { slug: slugByLocale.nl }),
  }
}

/** pageKey STATIQUE d'un pathname localisé, ou null si inconnu/dynamique. */
export function pageKeyForPath(pathname: string): StaticPageKey | null {
  const locale = (['en', 'nl'] as Locale[]).find(
    (l) => pathname === LOCALE_PREFIX[l] || pathname.startsWith(LOCALE_PREFIX[l] + '/'),
  )
  const activeLocale: Locale = locale ?? 'fr'
  const prefix = LOCALE_PREFIX[activeLocale]
  const rest = pathname.slice(prefix.length).replace(/^\//, '').replace(/\/$/, '') // slug sans '/' bordants
  for (const key of Object.keys(ROUTES) as StaticPageKey[]) {
    if (ROUTES[key][activeLocale] === rest) return key
  }
  return null
}
